"use client";

import { Calendar, User, ArrowLeft, Clock } from "lucide-react";

import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="2876" className="max-w-4xl mx-auto px-4 md:px-6 py-12">
        <div className="h-12 bg-slate-200 rounded-lg animate-pulse mb-6 w-3/4" />
        <div className="h-6 bg-slate-200 rounded-lg animate-pulse mb-8 w-1/2" />
        <div className="h-96 bg-slate-200 rounded-lg animate-pulse mb-8" />
        <div className="space-y-3">
          <div className="h-4 bg-slate-200 rounded animate-pulse" />
          <div className="h-4 bg-slate-200 rounded animate-pulse" />
          <div className="h-4 bg-slate-200 rounded animate-pulse w-5/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 text-center">
        <p className="text-slate-600 font-default">Unable to load this article. Please try again later.</p>
      </div>
    );
  }

  const post = posts && posts.length > 0 ? posts[0] : null;

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
        <h2 className="font-default text-3xl md:text-4xl text-slate-900 mb-4">Article Not Found</h2>
        <p className="text-slate-600 mb-8 font-default">The piece you are looking for may have been moved or no longer exists.</p>
        <Link to="https://vpngenix.com/go/NordVPN" newTab>
          <Button variant="outline" className="rounded-md border-slate-300">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Return Home
          </Button>
        </Link>
      </div>
    );
  }

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"] || "";

  return (
    <article className="font-default">
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-8 md:pt-12">
        <Link to="/VpnGenixPage" className="inline-flex items-center text-sm text-slate-600 hover:text-blue-600 transition-colors font-default mb-8">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Insights
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-12 md:mb-16">
          <div className="lg:col-span-7 lg:pt-8">
            {post.categories && post.categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {post.categories.slice(0, 3).map((cat, i) => (
                  <span
                    data-index={i}
                    key={cat.id}
                    className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-blue-100 text-blue-700 rounded-sm font-default"
                  >
                    {cat.name}
                  </span>
                ))}
              </div>
            )}

            <h1
              className="font-default text-4xl md:text-5xl lg:text-6xl text-slate-900 leading-tight mb-6 font-bold"
              dangerouslySetInnerHTML={{ __html: post.title }}
            />

            {post.excerpt && (
              <div
                className="text-lg md:text-xl text-slate-600 leading-relaxed font-default mb-8"
                dangerouslySetInnerHTML={{ __html: post.excerpt }}
              />
            )}

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-500 font-default">
              {post.author && (
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    {authorAvatar && <AvatarImage src={authorAvatar} alt={post.author.name} />}
                    <AvatarFallback className="bg-slate-200 text-slate-900 text-xs">
                      {post.author.name?.charAt(0) || "A"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-slate-900">{post.author.name}</span>
                </div>
              )}
              {formattedDate && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formattedDate}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>5 min read</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            {post.featuredImage ? (
              <div className="relative overflow-hidden rounded-lg shadow-md aspect-[4/5] lg:aspect-[3/4]">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : (
              <div className="relative overflow-hidden rounded-lg aspect-[4/5] lg:aspect-[3/4] bg-slate-100 flex items-center justify-center">
                <span className="text-slate-400 font-default">VPNGENIX</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="hidden lg:block lg:col-span-2">
            <div className="sticky top-24">
              <Separator className="bg-slate-200 mb-4" />
              <p className="text-xs uppercase tracking-widest text-slate-500 font-default">Article</p>
              {post.tags && post.tags.length > 0 && (
                <div className="mt-6 space-y-2">
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-default">Tagged</p>
                  <div className="flex flex-col gap-1">
                    {post.tags.slice(0, 5).map((tag, i) => (
                      <span
                        data-index={i}
                        key={tag.id}
                        className="text-sm text-slate-900 font-default"
                      >
                        — {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-8">
            {post.content ? (
              <div
                className="prose prose-lg max-w-none font-default text-slate-800 leading-relaxed [&_p]:mb-6 [&_p]:text-lg [&_p]:leading-[1.8] [&_h2]:font-default [&_h2]:text-3xl [&_h2]:md:text-4xl [&_h2]:mt-12 [&_h2]:mb-6 [&_h2]:text-slate-900 [&_h2]:font-bold [&_h3]:font-default [&_h3]:text-2xl [&_h3]:md:text-3xl [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:text-slate-900 [&_h3]:font-bold [&_a]:text-blue-600 [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-blue-800 [&_blockquote]:border-l-4 [&_blockquote]:border-blue-600 [&_blockquote]:pl-6 [&_blockquote]:my-8 [&_blockquote]:text-xl [&_blockquote]:text-slate-600 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ul_li]:mb-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_ol_li]:mb-2 [&_img]:rounded-lg [&_img]:my-8 [&_img]:shadow-md [&_strong]:text-slate-900 [&_strong]:font-bold"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            ) : (
              <p className="text-slate-500 font-default">No content available for this article.</p>
            )}

            {post.tags && post.tags.length > 0 && (
              <div className="lg:hidden mt-12 pt-8 border-t border-slate-200">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-default mb-3">Tagged</p>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, i) => (
                    <span
                      data-index={i}
                      key={tag.id}
                      className="inline-block px-3 py-1 text-xs bg-slate-100 text-slate-800 rounded-sm font-default"
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {post.author && (
              <div className="mt-16 md:mt-20">
                <Separator className="mb-10" />
                <Card className="bg-slate-50 border-0 shadow-md rounded-lg">
                  <CardContent className="p-6 md:p-8">
                    <div className="flex flex-col sm:flex-row gap-6 items-start">
                      <Avatar className="h-20 w-20 shrink-0">
                        {authorAvatar && <AvatarImage src={authorAvatar} alt={post.author.name} />}
                        <AvatarFallback className="bg-blue-600 text-white text-2xl font-default">
                          {post.author.name?.charAt(0) || "A"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-widest text-slate-500 font-default mb-2">Written by</p>
                        <h3 className="font-default text-2xl md:text-3xl text-slate-900 mb-3 font-bold">
                          {post.author.name}
                        </h3>
                        <p className="text-slate-600 font-default leading-relaxed mb-4">
                          Expert contributor at VPNGENIX, dedicated to providing clear, actionable insights into digital privacy and cybersecurity solutions.
                        </p>
                        {post.author.link && (
                          <Link to={post.author.link}>
                            <Button variant="outline" size="sm" className="rounded-md border-slate-300">
                              <User className="mr-2 h-4 w-4" />
                              View Profile
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          <div className="lg:col-span-2" />
        </div>
      </div>

      <RelatedPostsCluster currentPostId={post.id} categories={post.categories} />
    </article>
  );
}

function RelatedPostsList({ currentPostId }: { currentPostId: string }) {
  const { posts, loading } = usePosts();

  const related = posts.filter((p) => p.id !== currentPostId).slice(0, 5);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {[0, 1, 2].map((i) => (
          <div data-index={i} key={i} className="h-80 bg-slate-200 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (related.length === 0) {
    return (
      <p className="text-slate-500 font-default text-center">No related articles at this time.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
      {related.map((relatedPost, i) => {
        const relatedDate = relatedPost.date
          ? new Date(relatedPost.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";

        return (
          <Link
            data-index={i}
            key={relatedPost.id}
            to={relatedPost.link || "#"}
            className="group block"
          >
            <Card className="bg-white border border-slate-300 shadow-md rounded-lg overflow-hidden h-full transition-all hover:shadow-lg">
              <div className="relative aspect-[4/3] overflow-hidden">
                {relatedPost.featuredImage ? (
                  <img
                    src={relatedPost.featuredImage}
                    alt={relatedPost.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                    <span className="text-slate-400 font-default">VPNGENIX</span>
                  </div>
                )}
              </div>
              <CardContent className="p-6">
                {relatedPost.categories && relatedPost.categories.length > 0 && (
                  <p className="text-xs uppercase tracking-widest text-primary font-default mb-3">
                    {relatedPost.categories[0].name}
                  </p>
                )}
                <h3
                  className="font-default text-xl md:text-2xl text-slate-900 mb-3 leading-snug font-bold group-hover:text-primary transition-colors"
                  dangerouslySetInnerHTML={{ __html: relatedPost.title }}
                />
                {relatedDate && (
                  <p className="text-sm text-slate-500 font-default">{relatedDate}</p>
                )}
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}

interface ClusterConfig {
  name: string;
  categoryIds: number[];
  eyebrow: string;
  anchor_text: string;
}

const CLUSTERS: ClusterConfig[] = [
  {
    name: "Gaming VPNs",
    categoryIds: [12, 15, 22],
    eyebrow: "Low Latency Gaming",
    anchor_text: "More Gaming VPN Guides & Latency Benchmarks",
  },
  {
    name: "Business Security",
    categoryIds: [8, 14, 19],
    eyebrow: "Enterprise Sovereignty",
    anchor_text: "More Business VPN & Compliance Insights",
  },
  {
    name: "Streaming & Entertainment",
    categoryIds: [5, 11, 20],
    eyebrow: "Unrestricted Streaming",
    anchor_text: "More Streaming VPN & Unblocking Guides",
  },
  {
    name: "Privacy & Protocols",
    categoryIds: [3, 9, 16],
    eyebrow: "Digital Sovereignty",
    anchor_text: "More Cybersecurity & Encryption Protocols",
  },
];

function RelatedPostsCluster({ currentPostId, categories }: { currentPostId: string; categories?: { id: number; name: string }[] }) {
  const postCategoryIds = categories ? categories.map((c) => c.id) : [];

  let matchedCluster: ClusterConfig | null = null;
  for (const cluster of CLUSTERS) {
    if (cluster.categoryIds.some((id) => postCategoryIds.includes(id))) {
      matchedCluster = cluster;
      break;
    }
  }

  const queryParams = matchedCluster
    ? { post_type: "post", categories: matchedCluster.categoryIds, per_page: 5, status: ["publish"] }
    : { post_type: "post", per_page: 5, status: ["publish"] };

  const eyebrowText = matchedCluster ? matchedCluster.eyebrow : "Continue Reading";
  const headingText = matchedCluster ? matchedCluster.anchor_text : "Related Articles";

  return (
    <div className="bg-background mt-20 md:mt-28 py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-12 gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary bg-primary/10 px-2 py-1 rounded-sm inline-block font-default mb-3">
              {eyebrowText}
            </p>
            <h2 className="font-default text-3xl md:text-4xl lg:text-5xl text-slate-900 font-bold">
              {headingText}
            </h2>
          </div>
          <Link to="https://vpngenix.com/go/NordVPN" newTab>
            <Button variant="outline" className="rounded-md border-slate-300 font-default">
              View All Articles
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={queryParams}>
          <RelatedPostsList currentPostId={currentPostId} />
        </PostsProvider>
      </div>
    </div>
  );
}

export default function () {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-white text-slate-900 py-12 md:py-16">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}